import React, { useCallback, useEffect, useRef, useState } from 'react';
import './HeroMedia.css';

// Wrap the existing responsive <picture>; captions and hero copy remain outside.
// A missing/failed video retains that same image, including its alt text and LCP
// priority. The decorative video never replaces the accessible image content.
const HeroMedia = ({ src, poster, children, className = '' }) => {
  const mediaRef = useRef(null);
  const videoRef = useRef(null);
  const attemptRef = useRef(0);
  const requestedVideoRef = useRef(null);
  const [motionAllowed, setMotionAllowed] = useState(false);
  const [videoAllowed, setVideoAllowed] = useState(false);
  const [inView, setInView] = useState(false);
  const [hasEntered, setHasEntered] = useState(false);
  const [tabVisible, setTabVisible] = useState(true);
  const [userPaused, setUserPaused] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [playedSrc, setPlayedSrc] = useState(null);
  const [failedSrc, setFailedSrc] = useState(null);

  useEffect(() => {
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const update = () => {
      setMotionAllowed(!motion.matches);
      setVideoAllowed(!motion.matches && !connection?.saveData);
    };
    update();
    if (motion.addEventListener) motion.addEventListener('change', update);
    else motion.addListener?.(update);
    connection?.addEventListener?.('change', update);
    return () => {
      if (motion.removeEventListener) motion.removeEventListener('change', update);
      else motion.removeListener?.(update);
      connection?.removeEventListener?.('change', update);
    };
  }, []);

  useEffect(() => {
    const update = () => setTabVisible(document.visibilityState !== 'hidden');
    update();
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);

  useEffect(() => {
    if (!window.IntersectionObserver) {
      setInView(true);
      setHasEntered(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      setInView(entry.isIntersecting);
      if (entry.isIntersecting) setHasEntered(true);
    }, { threshold: 0.05 });
    observer.observe(mediaRef.current);
    return () => observer.disconnect();
  }, []);

  const failed = Boolean(src) && failedSrc === src;
  const mountVideo = Boolean(src) && videoAllowed && hasEntered && !failed;
  // Invalidate the current promise by advancing the shared generation. Cleanup
  // intentionally changes the latest generation, rather than a captured value.
  const invalidatePlay = useCallback(() => {
    requestedVideoRef.current = null;
    attemptRef.current += 1;
    return attemptRef.current;
  }, []);

  const requestPlay = useCallback(() => {
    const video = videoRef.current;
    if (!video || !videoAllowed || !inView || !tabVisible) return;
    if (requestedVideoRef.current === video) return;
    const attempt = invalidatePlay();
    requestedVideoRef.current = video;
    video.muted = true;
    const rejected = () => {
      if (attemptRef.current === attempt) {
        invalidatePlay();
        setPlaying(false);
      }
    };
    try {
      // Some browsers block autoplay even when muted. Keep the image and let
      // the keyboard-accessible control retry with an explicit user gesture.
      video.play()?.catch(rejected);
    } catch (_) { rejected(); }
  }, [videoAllowed, inView, tabVisible, invalidatePlay]);

  // Dispose only when this video element goes away or its source changes.
  // A user gesture may already have started playback before the state effect
  // runs; changing userPaused must not tear that successful request down.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    return () => {
      invalidatePlay();
      video.pause();
    };
  }, [mountVideo, src, invalidatePlay]);

  useEffect(() => {
    const video = videoRef.current;
    if (!mountVideo || !video) {
      setPlaying(false);
      setPlayedSrc(null);
      return;
    }
    if (userPaused || !inView || !tabVisible) {
      invalidatePlay();
      video.pause();
      setPlaying(false);
    } else requestPlay();
  }, [mountVideo, src, userPaused, inView, tabVisible, requestPlay, invalidatePlay]);

  const togglePlayback = () => {
    if (playing) {
      setUserPaused(true);
      invalidatePlay();
      videoRef.current?.pause();
      setPlaying(false);
    } else {
      setUserPaused(false);
      // Call in the click handler too, preserving browser user activation
      // when a previous autoplay attempt was rejected.
      requestPlay();
    }
  };

  const settledImage = motionAllowed && (!src || failed || !videoAllowed);
  return (
    <div ref={mediaRef}
      className={`hero-motion${settledImage ? ' hero-motion--settle' : ''}${playedSrc === src && mountVideo ? ' hero-motion--ready' : ''}${className ? ` ${className}` : ''}`}
      data-testid="hero-motion">
      <div className="hero-motion-still">{children}</div>
      {mountVideo && (
        <>
          <video key={src} ref={videoRef} className="hero-motion-video"
            src={src} poster={poster} muted loop playsInline preload="none"
            aria-hidden="true" tabIndex={-1} disablePictureInPicture
            onPlaying={() => {
              if (userPaused || !inView || !tabVisible || !videoAllowed) {
                videoRef.current?.pause();
                return;
              }
              setPlayedSrc(src);
              setPlaying(true);
            }}
            onPause={(event) => {
              if (event.currentTarget.paused) {
                invalidatePlay();
                setPlaying(false);
              }
            }}
            onError={() => { setFailedSrc(src); setPlaying(false); }} />
          <button type="button" className="hero-motion-toggle" data-testid="hero-motion-toggle"
            aria-label={playing ? 'Pause background animation' : 'Play background animation'}
            onClick={togglePlayback}>
            <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true" focusable="false">
              {playing ? <><rect x="5" y="4" width="3" height="12" rx=".7" /><rect x="12" y="4" width="3" height="12" rx=".7" /></>
                : <path d="M6 3.5 16 10 6 16.5Z" />}
            </svg>
            <span>{playing ? 'Pause' : 'Play'}</span>
          </button>
        </>
      )}
    </div>
  );
};

export default HeroMedia;
